import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { trackAddToCart } from '../lib/analytics';

const CartContext = createContext();

const cartReducer = (state, action) => {
  console.log('CartReducer called with:', action.type, action.payload);
  
  switch (action.type) {
    case 'ADD_TO_CART':
      // Create unique identifier using product id and variant
      const uniqueId = `${action.payload.id}_${action.payload.variant || 'default'}`;
      const existingItem = state.items.find(item => 
        item.id === action.payload.id && item.variant === action.payload.variant
      );
      
      if (existingItem) {
        const newState = {
          ...state,
          items: state.items.map(item =>
            (item.id === action.payload.id && item.variant === action.payload.variant)
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          ),
          lastActivity: Date.now() // Update last activity time
        };
        console.log('Updated existing item with variant, new state:', newState);
        return newState;
      } else {
        const newState = {
          ...state,
          items: [...state.items, { ...action.payload, uniqueId }],
          lastActivity: Date.now() // Update last activity time
        };
        console.log('Added new item with variant, new state:', newState);
        return newState;
      }

    case 'REMOVE_FROM_CART':
      console.log('Removing item with payload:', action.payload);
      return {
        ...state,
        items: state.items.filter(item => 
          !(item.id === action.payload.id && item.variant === action.payload.variant)
        ),
        lastActivity: Date.now() // Update last activity time
      };

    case 'UPDATE_QUANTITY':
      console.log('Updating quantity with payload:', action.payload);
      return {
        ...state,
        items: state.items.map(item =>
          (item.id === action.payload.id && item.variant === action.payload.variant)
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0),
        lastActivity: Date.now() // Update last activity time
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        lastActivity: null
      };

    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload.items || [],
        lastActivity: action.payload.lastActivity || Date.now()
      };

    case 'UPDATE_ACTIVITY':
      return {
        ...state,
        lastActivity: Date.now()
      };

    default:
      return state;
  }
};

// Load cart from localStorage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem('cart');
    const savedData = savedCart ? JSON.parse(savedCart) : null;
    
    if (savedData && savedData.lastActivity) {
      const now = Date.now();
      const timeDiff = now - savedData.lastActivity;
      const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
      
      // If more than 30 minutes have passed, return empty cart
      if (timeDiff > thirtyMinutes) {
        console.log('Cart expired after 30 minutes, clearing...');
        localStorage.removeItem('cart');
        return { items: [], lastActivity: null };
      }
      
      return savedData;
    }
    
    return { items: [], lastActivity: null };
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return { items: [], lastActivity: null };
  }
};

// Save cart to localStorage
const saveCartToStorage = (cart) => {
  try {
    const cartData = {
      items: cart.items,
      lastActivity: cart.lastActivity || Date.now()
    };
    localStorage.setItem('cart', JSON.stringify(cartData));
    console.log('Cart saved to localStorage:', cartData);
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

const initialState = {
  items: [],
  lastActivity: null
};

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCartData = loadCartFromStorage();
    if (savedCartData.items.length > 0 || savedCartData.lastActivity) {
      dispatch({
        type: 'LOAD_CART',
        payload: savedCartData
      });
      console.log('Loaded cart from localStorage:', savedCartData);
    }
  }, []);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    if (cart.items.length > 0 || cart.lastActivity) {
      saveCartToStorage(cart);
    }
  }, [cart]);

  // Set up auto-clear timer
  useEffect(() => {
    let timeoutId;

    const checkCartExpiry = () => {
      if (cart.lastActivity && cart.items.length > 0) {
        const now = Date.now();
        const timeDiff = now - cart.lastActivity;
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
        
        if (timeDiff >= thirtyMinutes) {
          console.log('Cart auto-cleared after 30 minutes of inactivity');
          dispatch({ type: 'CLEAR_CART' });
          localStorage.removeItem('cart');
        } else {
          // Set timeout for the remaining time
          const remainingTime = thirtyMinutes - timeDiff;
          timeoutId = setTimeout(checkCartExpiry, remainingTime);
        }
      }
    };

    // Start the timer if cart has items
    if (cart.items.length > 0 && cart.lastActivity) {
      checkCartExpiry();
    }

    // Cleanup timeout on unmount or cart change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [cart.lastActivity, cart.items.length]);

  // Update activity on user interactions (optional - for more granular tracking)
  useEffect(() => {
    const updateActivity = () => {
      if (cart.items.length > 0) {
        dispatch({ type: 'UPDATE_ACTIVITY' });
      }
    };

    // Listen for user interactions
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [cart.items.length]);

  const addToCart = (product, quantity = 1) => {
    console.log('addToCart called with:', product, quantity);
    dispatch({
      type: 'ADD_TO_CART',
      payload: { ...product, quantity }
    });
    trackAddToCart(product, quantity);
  };

  const removeFromCart = (productId, variant = null) => {
    console.log('removeFromCart called with:', productId, variant);
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: { id: productId, variant }
    });
  };

  const updateQuantity = (productId, quantity, variant = null) => {
    console.log('updateQuantity called with:', productId, quantity, variant);
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id: productId, quantity, variant }
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem('cart');
  };

  const getCartTotal = () => {
    return cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  // Get remaining time before cart expires
  const getCartExpiryTime = () => {
    if (!cart.lastActivity || cart.items.length === 0) return null;
    
    const now = Date.now();
    const timeDiff = now - cart.lastActivity;
    const thirtyMinutes = 30 * 60 * 1000;
    const remainingTime = thirtyMinutes - timeDiff;
    
    return remainingTime > 0 ? remainingTime : 0;
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    getCartExpiryTime
  };

  console.log('CartProvider rendering with cart:', cart);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
